import {AxiosRequestConfig, AxiosResponse} from "axios";

/**
 * One parsed server-sent event: its name and its payload, verbatim.
 *
 * `ended` marks the terminator, which comes as a message of its own and
 * carries no text.
 */
export interface StreamEvent {
    event: string;
    data: string;
    ended: boolean;
}

/** Terminator both streaming endpoints send once the answer is complete. */
export const STREAM_TERMINATOR = '</stream>';

/**
 * Where one message of the stream ends and the next begins.
 *
 * Both endpoints stream through Laravel's `eventStream`, which writes every
 * message as `event: <name>\ndata: <payload>\n\n` and puts the payload on the
 * wire as is, newlines included: a payload line after the first carries no
 * `data:` prefix, and a paragraph break inside the answer is itself a blank
 * line. A blank line therefore only ends a message when the next one starts
 * right after it. The SSE specification would drop those unprefixed lines,
 * which is why this parser follows the server's framing rather than the spec.
 */
const MESSAGE_BOUNDARY = /\n\n(?=event: )/;

/**
 * Parse one message of the stream, as delivered by the streaming adapter.
 *
 * The expected format is:
 * ```
 * event: update
 * data: <message content, newlines included>
 * ```
 *
 * Everything after `data: ` is the payload, up to the end of the message.
 */
export const parseStreamEvent = (message: string): StreamEvent => {
    let event = '';
    let data = message;

    if (data.startsWith('event: ')) {
        const lineEnd = data.indexOf('\n');

        event = data.slice('event: '.length, lineEnd === -1 ? undefined : lineEnd).trim();
        data = lineEnd === -1 ? '' : data.slice(lineEnd + 1);
    }

    if (data.startsWith('data: ')) {
        data = data.slice('data: '.length);
    }

    const ended = data === STREAM_TERMINATOR;

    return {event, data: ended ? '' : data, ended};
};

/**
 * A streaming request rejected on its status line.
 *
 * The adapter runs on `fetch`, so a failing status never becomes an
 * `AxiosError`: the shared response interceptor branches on
 * `error.response.status` and therefore stays silent, 401 and 403 included.
 * The status travels on the error so the caller can say something useful
 * about every failure rather than showing one generic message.
 */
export interface StreamError extends Error {
    status: number;
}

/**
 * Build the error thrown when a stream is refused.
 *
 * @param status
 * @param statusText
 */
const streamError = (status: number, statusText: string): StreamError =>
    Object.assign(new Error(`HTTP error! status: ${status}: ${statusText}`), {
        name: 'StreamError',
        status
    });

/**
 * HTTP status a streaming failure carries, when it carries one. A network
 * drop or an abort has none.
 *
 * @param error
 */
export const streamErrorStatus = (error: unknown): number | null => {
    const status = (error as StreamError | null)?.status;

    return typeof status === 'number' ? status : null;
};

/**
 * Create a streaming adapter for Axios to handle server-sent events (SSE).
 *
 * Axios cannot read a response body progressively, so the request is issued
 * with `fetch` and the body is handed to `onChunk` one whole message at a
 * time, whatever the network reads cut it into. A message is only known to be
 * complete once the next one starts, so each waits for the following one, and
 * the last for the end of the stream. The returned response carries no data:
 * everything has already been delivered.
 *
 * Beware that this bypasses the shared response interceptor — the caller owns
 * the handling of every failing status.
 *
 * @param onChunk
 * @param onError
 */
export const createStreamingAdapter = (
    onChunk: (text: string) => void,
    onError: (error: unknown) => void
) => {
    return async (
        config: AxiosRequestConfig
    ): Promise<AxiosResponse> => {
        const response = await fetch(config.baseURL + "/" + config.url, {
            method: config.method?.toUpperCase() || 'GET',
            headers: config.headers as HeadersInit,
            body: config.data,
            signal: config.signal as AbortSignal | undefined,
        });

        if (!response.ok) {
            throw streamError(response.status, response.statusText);
        }

        const reader = response.body?.getReader();

        if (!reader) {
            throw new Error('Response body is not readable');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        const deliver = (message: string) => {
            if (onChunk && message.trim()) {
                onChunk(message);
            }
        };

        try {
            while (true) {
                const {done, value} = await reader.read();

                if (done) {
                    break;
                }

                buffer += decoder.decode(value, {stream: true});

                const messages = buffer.split(MESSAGE_BOUNDARY);

                buffer = messages.pop() ?? '';
                messages.forEach(deliver);
            }

            buffer += decoder.decode();

            // Only the blank line closing the stream goes: a newline ending the
            // answer belongs to it.
            deliver(buffer.endsWith('\n\n') ? buffer.slice(0, -2) : buffer);
        } catch (error) {
            if (onError) {
                onError(error);
            }
        } finally {
            reader.releaseLock();
        }

        return {
            data: null,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
            config: config,
        } as unknown as AxiosResponse;
    };
};
